import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  WorkContractDto,
  WorkContractUpdate,
} from "../../../api-model/employees/workContract";
import { ApiCallState, GenericError } from "../../../api-model/api";
import { CoreRequest } from "../../../config/serviceInstances";
import { ActionType, handleActionWithFeedback } from "../../../store/errors";
import { WorkContractAdditionalDataFilter } from "../../../api-model/employees/employee";

export const EMPLOYEE_WORK_CONTRACTS_GET =
  "employees/current/workcontracts/get";

export interface WorkContractsState extends ApiCallState {
  data: WorkContractDto[];
  fulfilled: boolean;
}

export interface WorkContractState extends ApiCallState {
  data: WorkContractDto | Record<string, never>;
}

export const getCurrentEmployeeWorkContracts = createAsyncThunk<
  WorkContractDto[],
  string,
  { rejectValue: GenericError }
>(EMPLOYEE_WORK_CONTRACTS_GET, async (employeeId, thunkApi) => {
  const response = await CoreRequest.get(`/contracts/employee/${employeeId}`);

  if (response.status > 299) {
    // Return the known error for future handling
    return thunkApi.rejectWithValue(response.data as GenericError);
  }

  return response.data as WorkContractDto[];
});

type EmployeeWorkContractParam = {
  workContractId: string;
  extraFilters?: WorkContractAdditionalDataFilter[];
};

export const getCurrentEmployeeWorkContract = createAsyncThunk<
  WorkContractDto,
  EmployeeWorkContractParam,
  { rejectValue: GenericError }
>(
  "/employeeDetail/workcontract/basic",
  async ({ workContractId, extraFilters }, thunkApi) => {
    const response = await CoreRequest.get(`/contracts/${workContractId}`, {
      params: {
        withData: [
          WorkContractAdditionalDataFilter.TIMETRACKING,
          WorkContractAdditionalDataFilter.ABSENCE,
          ...(extraFilters ?? []),
        ].join(","),
      },
    });

    if (response.status > 299) {
      // Return the known error for future handling
      return thunkApi.rejectWithValue(response.data as GenericError);
    }

    return response.data as WorkContractDto;
  }
);

export const updateWorkContract = createAsyncThunk<
  WorkContractDto,
  { workContract: WorkContractUpdate },
  { rejectValue: GenericError }
>("/employeeDetail/workcontract/update", async (arg, thunkApi) => {
  return handleActionWithFeedback<WorkContractDto>(
    () => CoreRequest.put(`/contracts/workcontract`, arg.workContract),
    thunkApi,
    ActionType.update,
    "resource.employee"
  );
});

export const deleteWorkContract = createAsyncThunk<WorkContractDto, string>(
  "/employeeDetail/workcontract/delete",
  async (arg, thunkApi) => {
    return handleActionWithFeedback<WorkContractDto>(
      () => CoreRequest.delete(`/contracts/${arg}`),
      thunkApi,
      ActionType.delete,
      "resource.employee"
    );
  }
);
